import { useMatchRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo } from 'react';

import { guard, merge } from '@/components';
import { ErrorCard, Heading, Save, Separator, Table, Text } from '@/components/core';
import { TriggerCell, TriggerOption, TRIGGERS } from '@/components/flow';
import { DivProps, Id, OmitChildren } from '@/utils';

export type OnTriggerOptionSelected = (triggerOption: TriggerOption) => void;

export type NewTriggerProps = OmitChildren<DivProps> & {
  onTriggerOptionSelected?: OnTriggerOptionSelected;
  isAddingTrigger?: boolean;
  errors?: Error[];
};

const INewTrigger = ({ onTriggerOptionSelected, isAddingTrigger, errors, className, ...rest }: NewTriggerProps) => {
  const matchRoute = useMatchRoute();
  const includeFakes = matchRoute({ to: '/agents/new' });

  const triggerData: (TriggerOption & Id)[] = useMemo(() => {
    return TRIGGERS.filter((e) => (!includeFakes ? !e.isFakeImport : true)).map((t) => ({ ...t, id: t.functionId }));
  }, [includeFakes]);

  const triggerColumns = useMemo<ColumnDef<TriggerOption & Id>[]>(() => {
    return [
      {
        accessorKey: 'functionId',
        cell: ({ row }) => (
          <TriggerCell
            onPress={() => onTriggerOptionSelected?.(row.original)}
            option={row.original}
            isDisabled={!!row.original.badge}
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, [onTriggerOptionSelected]);

  return (
    <div className={merge('gap-md p-md flex flex-col', className)} {...rest}>
      <div className="gap-xs flex flex-col">
        <Heading slot="title">New trigger</Heading>
        <Text>Where are your documents coming from?</Text>
      </div>
      <Separator orientation="horizontal" />
      {guard(isAddingTrigger, () => (
        <Save message="Setting up trigger" />
      ))}
      {errors ? (
        <div className="h-fit">
          <ErrorCard label="Failed to add trigger" error={errors} />
        </div>
      ) : null}
      {/* TODO: Maybe use ternary statement instead */}
      {guard(!isAddingTrigger, () => (
        <div className="overflow-y-auto">
          <Table
            className="border-separate border-spacing-2"
            columns={triggerColumns}
            data={triggerData}
            hideHeader
            hideDragHandle
            hideRowOptions
          />
        </div>
      ))}
    </div>
  );
};

export const NewTrigger = memo(INewTrigger) as typeof INewTrigger;
